/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import cx from 'classnames';

import { SortIcon, IconInfoCircle, Tooltip } from '../..';

interface InfoTooltipHeaderProps<TData extends object> {
    column: SortableColumn<TData>;
}

/**
 * Displays a header for a table column with an info icon that triggers a tooltip to display some
 * helpful information about the column. The info shown in the tooltip is expected to be defined
 * on the column object as the `headerInfo` property.
 * @param props {@link InfoTooltipHeaderProps}
 * @returns JSX.Element
 */
export function InfoTooltipHeader<TData extends object>({
    column,
}: InfoTooltipHeaderProps<TData>): JSX.Element {
    const {
        isSorted,
        isSortedDesc,
        headerLabel,
        headerInfo,
        tooltipMinWidth,
        tooltipMaxWidth,
    } = column;

    const headerClasses = cx('font-semibold text-xs', {
        'text-black': isSorted,
    });

    return (
        <div className={headerClasses}>
            <div className="w-full h-full mb-1">
                <div className={cx({ underline: isSorted })}>
                    <span className="pr-1">{headerLabel}</span>
                    <Tooltip
                        addClasses="respond-to-mouse mr-1"
                        background="light"
                        message={
                            <div
                                className="text-tertiary-400 font-normal grid my-2"
                                style={{ gridTemplateColumns: '26px auto' }}
                            >
                                <IconInfoCircle
                                    fillColor="tertiary-400"
                                    width={16}
                                />
                                <p>{headerInfo}</p>
                            </div>
                        }
                        zIndex={100}
                        minWidth={tooltipMinWidth ?? 250}
                        maxWidth={tooltipMaxWidth ?? 250}
                    >
                        <IconInfoCircle fillColor="tertiary-400" width={16} />
                    </Tooltip>
                    <SortIcon isSorted={isSorted} isSortedDesc={isSortedDesc} />
                </div>
            </div>
        </div>
    );
}
